import Add16 from '@carbon/icons-react/es/add/16';
import { Col, Row } from 'antd';
import { FormListOperation } from 'antd/es/form/FormList';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { camelCase } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { components, OptionProps } from 'react-select';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { RATING_SCALE, SingleAndMultiSelectFieldMeta } from 'api/fields/fields';
import { ObjectType } from 'api/pushes';
import { Field } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { Label } from 'components/atoms/label';
import { PageLoading } from 'components/atoms/loading';
import { Select } from 'components/atoms/select';
import { Switch } from 'components/atoms/switch/switch';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useAllFieldsQuery } from 'queries';
import { useFieldSectionsQuery } from 'queries/field-sections';
import { getRandomColor } from 'utils/get-random-color';
import { FieldOption } from './field';
import {
  addOption,
  addStatusOption,
  SortableOptions,
  SortableStatusOptions,
} from './options';
import { FieldTypeOption, Option, Values } from './types';
import {
  getFieldNamePlaceholderKey,
  getFieldSections,
  getFieldTypeOptions,
  getInitialValues,
  isProjectStatusField,
  isRatingField,
  isRatingFieldTypeOption,
  isSelectField,
  isTaskStatusField,
} from './utils';

const OptionStyled = styled.div<{
  isPrivateSection: boolean;
  isTaskType: boolean;
}>`
  .ozone-select__option {
    background-color: ${({ isPrivateSection, isTaskType }) =>
      isPrivateSection && isTaskType && 'var(--private-01)'};

    &:hover {
      background-color: ${({ isPrivateSection, isTaskType }) =>
        isPrivateSection && isTaskType && 'var(--private-02)'};
    }
  }

  .ozone-select__option--is-selected {
    background-color: ${({ isPrivateSection, isTaskType }) =>
      isPrivateSection && isTaskType && 'var(--primary-color) !important'};
    color: #f9f9fa;
  }
`;

const RATING_FIELDS_OPTIONS = RATING_SCALE.map((e) => ({
  label: e.toString(),
  value: e,
}));

const SectionOptionComp = (props: OptionProps<Option, false>) => {
  const { data } = props;

  return (
    <OptionStyled
      isPrivateSection={data.private as boolean}
      isTaskType={data.sectionType === ObjectType.Task}
    >
      <components.Option {...props}>
        <div>{data.label}</div>
      </components.Option>
    </OptionStyled>
  );
};

const OptionComp = (props: OptionProps<Option, false>) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <span data-cy={`settings_field_type_${data.value}`}>
        <FieldOption type={data.value} />
      </span>
    </components.Option>
  );
};

const StyledFormItem = styled(Form.Item)<{ isTaskType?: boolean }>`
  margin-top: ${({ isTaskType }) => !isTaskType && 'var(--spacing-07)'};
`;

const StyledNote = styled.div`
  width: 100%;
`;

export const FieldForm: FC<{
  form: FormInstance<Values>;
  field?: Field;
  objectType: Field['objectType'];
}> = ({ form, field, objectType }) => {
  const translate = useTranslate();
  const { data: allFields } = useAllFieldsQuery();
  const projectFieldNameToBeCheck = useMemo(
    () =>
      (allFields ?? [])
        .filter(
          (x) =>
            x.objectType === FieldSectionType.Project &&
            x.fieldId !== field?.fieldId,
        )
        .map(({ fieldLabel }) => camelCase(fieldLabel?.trim())?.toLowerCase()),
    [allFields, field?.fieldId],
  );
  const taskFieldNameToBeCheck = useMemo(
    () =>
      (allFields ?? [])
        .filter(
          (x) =>
            x.objectType === FieldSectionType.Task &&
            x.fieldId !== field?.fieldId,
        )
        .map(({ fieldLabel }) => camelCase(fieldLabel?.trim())?.toLowerCase()),
    [allFields, field?.fieldId],
  );
  const teamMemberFieldNameToBeCheck = useMemo(
    () =>
      (allFields ?? [])
        .filter(
          (x) =>
            x.objectType === FieldSectionType.Users &&
            x.fieldId !== field?.fieldId,
        )
        .map(({ fieldLabel }) => camelCase(fieldLabel?.trim())?.toLowerCase()),
    [allFields, field?.fieldId],
  );

  const isProjectForm = objectType === FieldSectionType.Project;
  const isTaskForm = objectType === FieldSectionType.Task;
  const isUserForm = objectType === FieldSectionType.Users;
  const isSectionForm = Boolean(objectType);
  const isFieldSectionQueryEnabled = isSectionForm;
  const { data: fieldSections, isLoading: isFieldSectionsLoading } =
    useFieldSectionsQuery(
      objectType ?? FieldSectionType.Task,
      isFieldSectionQueryEnabled,
    );

  const isStatusField =
    field != null && (isTaskStatusField(field) || isProjectStatusField(field));
  const isCurrentFieldRating =
    isRatingFieldTypeOption(form.getFieldValue('type')) || isRatingField(field);
  const isEdit = Boolean(field);
  const canShowFieldSection =
    isSectionForm && fieldSections && fieldSections.length > 1;

  const handleValuesChange = async ({ type }) => {
    await form.validateFields();
    if (type) {
      if (isSelectField(type)) {
        form.setFieldsValue({
          metaData: {
            options: [
              {
                label: '',
                color: getRandomColor(0),
                deletable: true,
                editable: true,
              },
            ],
          },
        });
      } else {
        form.setFieldsValue({
          metaData: undefined,
        });
      }
    }
  };

  const requiredMessage = useTranslation('input.required');
  const uniqueErrorMessage = useTranslation(
    'settings.fields.create.unique_error',
  );

  return isFieldSectionsLoading ? (
    <PageLoading />
  ) : (
    <Form
      form={form}
      name="new_field_form"
      autoComplete="off"
      initialValues={getInitialValues(
        translate,
        objectType,
        field,
        fieldSections,
      )}
      preserve={false}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      {() => (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fieldLabel"
                label={<FormattedMessage id="settings.fields.field.name" />}
                rules={[
                  { required: true, message: requiredMessage },
                  () => ({
                    validator(_, value) {
                      const namesToCheck = isProjectForm
                        ? projectFieldNameToBeCheck
                        : isTaskForm
                        ? taskFieldNameToBeCheck
                        : teamMemberFieldNameToBeCheck;

                      if (
                        !namesToCheck.includes(
                          camelCase(value.trim())?.toLowerCase(),
                        )
                      ) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error(uniqueErrorMessage));
                    },
                  }),
                ]}
                data-cy="form-items.field-label"
              >
                <Input
                  data-cy="settings_fields_fieldName_input"
                  disabled={isStatusField}
                  name="fieldLabel"
                  autoFocus
                  placeholder={translate(
                    getFieldNamePlaceholderKey(
                      form.getFieldValue('type')?.value,
                    ),
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label={<FormattedMessage id="settings.fields.field.type" />}
              >
                <Select
                  isClearable={false}
                  isDisabled={isEdit}
                  options={getFieldTypeOptions(translate, objectType)}
                  components={{ Option: OptionComp }}
                />
              </Form.Item>
            </Col>
          </Row>
          {!isStatusField && objectType === FieldSectionType.Task && (
            <StyledFormItem
              name="private"
              shouldUpdate
              label={<FormattedMessage id="settings.fields.field.private" />}
              valuePropName="checked"
              isTaskType
            >
              <Switch
                data-cy="task_field_privacy_toggle"
                disabled={isProjectForm || isUserForm}
              />
            </StyledFormItem>
          )}
          {canShowFieldSection && (
            <Form.Item
              name="fieldSection"
              label={<FormattedMessage id="settings.fields.section.name" />}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (
                      value.private &&
                      value.sectionType === ObjectType.Task &&
                      !getFieldValue('private')
                    ) {
                      return Promise.reject(
                        new Error(
                          translate(
                            'settings.fields.error.cannot.be.part.pf.private.section',
                          ),
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select
                isClearable={false}
                options={getFieldSections(fieldSections, objectType)}
                components={{ Option: SectionOptionComp }}
              />
            </Form.Item>
          )}
          <Form.Item
            name="description"
            label={<FormattedMessage id="description" />}
            help={
              <FormattedMessage id="settings.fields.field.description.hint" />
            }
          >
            <TextArea
              data-cy="settings_fields_description_input"
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          </Form.Item>
          {!isStatusField && objectType !== FieldSectionType.Task && (
            <StyledFormItem
              name="private"
              label={
                objectType === FieldSectionType.Project ? (
                  <FormattedMessage id="settings.fields.field.private.project" />
                ) : objectType === FieldSectionType.Users ? (
                  <FormattedMessage id="settings.fields.field.private.team_members" />
                ) : (
                  <FormattedMessage id="settings.fields.field.private" />
                )
              }
              valuePropName="checked"
            >
              <Switch
                data-cy="settings_fields_privacy_toggle"
                disabled={isProjectForm || isUserForm}
              />
            </StyledFormItem>
          )}

          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const isFieldTypeRating =
                isCurrentFieldRating ||
                isRatingFieldTypeOption(getFieldValue('type'));

              return isFieldTypeRating ? (
                <Form.Item
                  name={['metaData', 'ratingScale']}
                  label={translate('no_of_stars')}
                  rules={[
                    {
                      required: isFieldTypeRating,
                      message: translate('rating_fields.select_scale'),
                    },
                    () => ({
                      validator(_, option) {
                        if (Boolean(option) && Boolean(option.value)) {
                          return Promise.resolve();
                        }

                        return Promise.reject();
                      },
                    }),
                  ]}
                >
                  <Select
                    isDisabled={field?.fieldId}
                    options={RATING_FIELDS_OPTIONS}
                    isClearable
                  />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const type = getFieldValue('type') as FieldTypeOption;

              return isSelectField(type) ? (
                <div>
                  <Label>
                    <FormattedMessage id="choices" />
                  </Label>
                  <Form.List name={['metaData', 'options']}>
                    {(
                      fields,
                      { add: addField, remove, move }: FormListOperation,
                    ) => {
                      const onSortEnd = ({ oldIndex, newIndex }) => {
                        move(oldIndex, newIndex);
                      };

                      const add = isStatusField
                        ? addStatusOption(addField, objectType)
                        : addOption(addField);

                      const OptionsListComp = isStatusField
                        ? SortableStatusOptions
                        : SortableOptions;

                      return (
                        <>
                          <OptionsListComp
                            useDragHandle
                            form={form}
                            fields={fields}
                            add={add}
                            remove={remove}
                            onSortEnd={onSortEnd}
                            objectType={objectType}
                            options={
                              (
                                getFieldValue(
                                  'metaData',
                                ) as SingleAndMultiSelectFieldMeta
                              )?.options
                            }
                          />

                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add(fields.length)}
                              block
                              icon={<Add16 />}
                            >
                              <FormattedMessage id="settings.fields.add_option" />
                            </Button>
                          </Form.Item>
                        </>
                      );
                    }}
                  </Form.List>
                </div>
              ) : null;
            }}
          </Form.Item>

          <Form.Item hidden name="objectType">
            <Input type="hidden" />
          </Form.Item>

          {isCurrentFieldRating ? (
            <StyledNote>
              {translate('field_form.rating_fields.note', {
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </StyledNote>
          ) : null}
        </>
      )}
    </Form>
  );
};
